import { useCallback, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';
import { RestError, useAuthedRest } from '@/api/useAuthedRest';

import { useGraphQLQuery } from './useGraphQLQuery';

// Mirrors AdminRoasterResponse from the API
// (apps/api-cf/src/app/roaster/roaster.service.ts). These shop fields are
// admin-only — they exist on no public (GraphQL) roaster surface.
export interface AdminRoaster {
  id: string;
  name: string;
  city: string;
  countryCode: string;
  location: string;
  shopUrl: string | null;
  shopPlatformType: string | null;
  // False for a roaster that sells no coffee online at all. Such a roaster has
  // no shop fields to fill in, so it never belongs in the "needs info" queue.
  hasWebshop: boolean;
  // Count of staged (pending) crawl listings awaiting review — drives the
  // "Review N" pill on the row.
  pendingListings: number;
}

// What the endpoint may actually return: responses are cast, not validated, and
// a Worker predating migration 0015 serves no `hasWebshop` at all — the web app
// and the API deploy from separate workflows, so that window is real.
type AdminRoasterPayload = Omit<AdminRoaster, 'hasWebshop'> & { hasWebshop?: boolean };

// Fills in a missing flag at the boundary, matching the column's own DEFAULT
// true: unknown means "assume it has a shop". Without this an absent field
// would read as false everywhere downstream, flipping every roaster to
// "no webshop" and emptying the needs-info queue.
function normalizeRoaster(row: AdminRoasterPayload): AdminRoaster {
  return { ...row, hasWebshop: row.hasWebshop ?? true };
}

// 'complete'    — indexed: has both a shop URL and a platform.
// 'no-webshop'  — deliberately set aside: sells nothing online.
// 'needs'       — still missing shop details.
export type RoasterStatus = 'complete' | 'no-webshop' | 'needs';

export function roasterStatus(roaster: AdminRoaster): RoasterStatus {
  if (!roaster.hasWebshop) return 'no-webshop';
  return roaster.shopUrl && roaster.shopPlatformType ? 'complete' : 'needs';
}

// Whether a roaster is done with — either indexed or set aside. This is what
// the "Complete" tab shows; the two states are told apart by the row badge.
export function isRoasterComplete(roaster: AdminRoaster): boolean {
  return roasterStatus(roaster) !== 'needs';
}

const GENERIC_SAVE_ERROR = "Couldn't save — try again";

interface UseRoastersReturn {
  roasters: AdminRoaster[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<AdminRoaster[] | null>;
}

// Reuses the transport-agnostic useGraphQLQuery machinery over the REST
// admin endpoint, exactly like useAllowlist.
export function useRoasters(): UseRoastersReturn {
  const rest = useAuthedRest();

  const { data, isLoading, error, refetch } = useGraphQLQuery<AdminRoaster[]>({
    fetcher: (signal) =>
      rest
        .request<AdminRoasterPayload[]>('/admin/roaster', { signal })
        .then((rows) => rows.map(normalizeRoaster)),
    deps: [],
  });

  return { roasters: data, isLoading, error, refetch };
}

// Mirrors the PATCH /admin/roaster/:id body union: either shop details (which
// also clears a previous "no webshop" mark) or the set-aside flag on its own.
export type UpdateRoasterInput =
  | { shopUrl: string; shopPlatformType: string }
  | { hasWebshop: false };

// `message: null` means an Auth0 redirect is in flight — render no error.
export type UpdateRoasterResult =
  | { ok: true; roaster: AdminRoaster }
  | { ok: false; message: string | null };

interface UseUpdateRoasterReturn {
  updateRoaster: (id: string, input: UpdateRoasterInput) => Promise<UpdateRoasterResult>;
  isSubmitting: boolean;
}

// Returns a result union (like useAddAllowlistEntry) so the row can render an
// inline validation error (e.g. a rejected URL, 400) next to the field.
export function useUpdateRoaster(): UseUpdateRoasterReturn {
  const rest = useAuthedRest();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateRoaster = useCallback(
    async (id: string, input: UpdateRoasterInput): Promise<UpdateRoasterResult> => {
      setIsSubmitting(true);
      try {
        const roaster = await rest.request<AdminRoasterPayload>(`/admin/roaster/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        });
        return { ok: true, roaster: normalizeRoaster(roaster) };
      } catch (err) {
        if (err instanceof AuthRedirectError) {
          return { ok: false, message: null };
        }
        if (err instanceof RestError && err.status === 400) {
          return { ok: false, message: err.message || GENERIC_SAVE_ERROR };
        }
        return { ok: false, message: GENERIC_SAVE_ERROR };
      } finally {
        setIsSubmitting(false);
      }
    },
    [rest],
  );

  return { updateRoaster, isSubmitting };
}
